import {
  popMenu,
  popupTargetFromElement,
} from '@blocksuite/affine-components/context-menu';
import { DatePicker } from '@blocksuite/affine-components/date-picker';
import { createLitPortal } from '@blocksuite/affine-components/portal';
import { IS_MOBILE } from '@blocksuite/global/env';
import { flip, offset, shift } from '@floating-ui/dom';
import { computed, signal } from '@preact/signals-core';
import { format } from 'date-fns/format';
import { html } from 'lit';

import { BaseCellRenderer } from '../../core/property/index.js';
import { createFromBaseCellRenderer } from '../../core/property/renderer.js';
import { createIcon } from '../../core/utils/uni-icon.js';
import {
  dateCellStyle,
  datePickerContainerStyle,
  dateValueContainerStyle,
} from './cell-renderer-css.js';
import {
  datePropertyModelConfig,
  normalizeDate,
  type NormalizedDate,
} from './define.js';

export class DateCell extends BaseCellRenderer<any, any> {
  private _prevPortalAbortController: AbortController | null = null;

  private readonly openDatePicker = () => {
    if (
      this._prevPortalAbortController &&
      !this._prevPortalAbortController.signal.aborted
    )
      return;

    this.tempValue$.value = normalizeDate(this.value) ?? undefined;

    this._prevPortalAbortController?.abort();
    const abortController = new AbortController();
    abortController.signal.addEventListener(
      'abort',
      () => {
        this.selectCurrentCell(false);
      },
      { once: true }
    );
    this._prevPortalAbortController = abortController;

    const setupDatePicker = () => {
      const datePicker = new DatePicker();
      const current = this.tempValue$.value;
      datePicker.value = current?.start ?? Date.now();
      if (current?.end) {
        datePicker.range = true;
        datePicker._rangeStart = new Date(current.start);
        datePicker._rangeEnd = new Date(current.end);
      } else if (current?.start) {
        datePicker._rangeStart = new Date(current.start);
      }
      datePicker.showTime = current?.hasTime ?? false;

      datePicker.onClear = () => {
        this.tempValue$.value = undefined;
      };
      datePicker.onChange = (date: Date) => {
        this.tempValue$.value = {
          start: date.getTime(),
          hasTime: datePicker.showTime,
        };
      };
      datePicker.onChangeRange = ({ start, end, hasTime }) => {
        this.tempValue$.value = {
          start: start.getTime(),
          end: end?.getTime(),
          hasTime: !!hasTime,
        };
      };
      datePicker.onEscape = () => {
        abortController.abort();
      };

      const toggleRange = (e: Event) => {
        const checked = (e.target as HTMLInputElement).checked;
        datePicker.range = checked;
        const c = this.tempValue$.value;
        if (c)
          this.tempValue$.value = { ...c, end: checked ? c.end : undefined };
      };

      const toggleTime = (e: Event) => {
        const checked = (e.target as HTMLInputElement).checked;
        datePicker.showTime = checked;
        const c = this.tempValue$.value;
        if (c) this.tempValue$.value = { ...c, hasTime: checked };
      };

      return { datePicker, toggleRange, toggleTime };
    };

    if (IS_MOBILE) {
      popMenu(popupTargetFromElement(this), {
        options: {
          title: {
            text: this.property.name$.value,
          },
          onClose: () => {
            abortController.abort();
          },
          items: [
            () =>
              html` <div class="${dateValueContainerStyle}">
                ${this.formattedTempValue$.value}
              </div>`,
            () => {
              const { datePicker, toggleRange, toggleTime } = setupDatePicker();
              datePicker.padding = 0;
              requestAnimationFrame(() => datePicker.focusDateCell());
              return html` <div
                class="${datePickerContainerStyle}"
                style="display:flex; flex-direction:column;"
              >
                ${datePicker}
                <div
                  style="display:flex; padding: 12px 16px; gap: 24px; border-top: 1px solid var(--affine-border-color);"
                >
                  <label
                    style="display:flex; align-items:center; gap:8px; font-size:14px;"
                    ><input
                      type="checkbox"
                      .checked=${datePicker.range}
                      @change=${toggleRange}
                    />
                    Range</label
                  >
                  <label
                    style="display:flex; align-items:center; gap:8px; font-size:14px;"
                    ><input
                      type="checkbox"
                      .checked=${datePicker.showTime}
                      @change=${toggleTime}
                    />
                    Time</label
                  >
                </div>
              </div>`;
            },
          ],
        },
      });
    } else {
      const { portal } = createLitPortal({
        abortController,
        closeOnClickAway: true,
        computePosition: {
          referenceElement: this,
          placement: 'bottom',
          middleware: [offset(10), flip(), shift({ padding: 8 })],
        },
        template: () => {
          const { datePicker, toggleRange, toggleTime } = setupDatePicker();
          datePicker.popup = true;
          requestAnimationFrame(() => datePicker.focusDateCell());
          return html`<div
            class="date-cell-popover-wrapper"
            style="display:flex; flex-direction:column; background: var(--affine-background-overlay-panel-color); border-radius: 8px; box-shadow: var(--affine-popover-shadow); overflow:hidden;"
          >
            ${datePicker}
            <div
              style="display:flex; padding: 8px 16px; gap: 16px; border-top: 1px solid var(--affine-border-color);"
            >
              <label
                style="display:flex; align-items:center; gap:4px; font-size:12px; cursor:pointer;"
                ><input
                  type="checkbox"
                  .checked=${datePicker.range}
                  @change=${toggleRange}
                />
                Range</label
              >
              <label
                style="display:flex; align-items:center; gap:4px; font-size:12px; cursor:pointer;"
                ><input
                  type="checkbox"
                  .checked=${datePicker.showTime}
                  @change=${toggleTime}
                />
                Time</label
              >
            </div>
          </div>`;
        },
      });
      portal.style.zIndex = '1002';
    }
  };

  private readonly updateValue = () => {
    const tempValue = this.tempValue$.value;
    const currentValue = normalizeDate(this.value);

    if (!tempValue && !currentValue) {
      return;
    }
    if (
      tempValue &&
      currentValue &&
      tempValue.start === currentValue.start &&
      tempValue.end === currentValue.end &&
      tempValue.hasTime === currentValue.hasTime
    ) {
      return;
    }

    this.valueSetNextTick(
      tempValue
        ? {
            start: tempValue.start,
            end: tempValue.end,
            hasTime: tempValue.hasTime,
          }
        : null
    );
    this.tempValue$.value = undefined;
  };

  tempValue$ = signal<NormalizedDate | undefined>();

  format(value?: NormalizedDate) {
    if (!value) return '';
    const fmt = value.hasTime ? 'yyyy/MM/dd HH:mm' : 'yyyy/MM/dd';
    const startStr = format(value.start, fmt);
    if (value.end) {
      return `${startStr} - ${format(value.end, fmt)}`;
    }
    return startStr;
  }

  formattedTempValue$ = computed(() => {
    return this.format(this.tempValue$.value);
  });
  formattedValue$ = computed(() => {
    return (
      this.formattedTempValue$.value ||
      this.format(normalizeDate(this.value) ?? undefined)
    );
  });

  override afterEnterEditingMode() {
    this.openDatePicker();
  }

  override beforeExitEditingMode() {
    this.updateValue();
    this._prevPortalAbortController?.abort();
  }

  override render() {
    return html` <div class="${dateCellStyle} date">
      ${this.formattedValue$.value}
    </div>`;
  }
}

export const datePropertyConfig = datePropertyModelConfig.createPropertyMeta({
  icon: createIcon('DateTimeIcon'),
  cellRenderer: {
    view: createFromBaseCellRenderer(DateCell),
  },
});
